import { parseQueryParamsObject } from "@robe/core-utils";
import { Content } from "@robe/core-types/content";
import { XcoreCore } from "XcoreCore";
import { resolveFetchPublishedOnly, resolveLocaleParams } from "./utils/queryParamsResolvers";
import { resolveUnfetchedValuesToContentValuesForContent } from "./utils/resolveAndAssignUnfetchedValues";
import { resolveContentSubCategories } from "./utils/subCategories";
import { BaseApiRequest } from "@robe/core-api/baseApiRequest";
import { NotFoundError } from "../errors";

/**
 * Options for fetching content, including whether to fetch in preview mode, fetch rich text links, and specify relations.
 *
 * @typeParam T A type extending `Content`, representing the structure of the content to be fetched.
 *
 * @property {boolean} [publishedOnly] In web only published contents are being fetched by default
 * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
 * @property {string[]} [locales] Specify locale for which content should be fetched.
 * @property {boolean} [includeValues] Whether content values should be included in fetched content.
 * @property {boolean} [includeCategories] Whether content categories should be included in fetched content.
 * @property {((keyof T["values"]) & string)[]} [fields] (Optional), list of specific content fields to fetch.
 */
export interface GetContentOptions<T extends Content> {
  publishedOnly?: boolean;
  locales?: string[];
  includeValues?: boolean;
  includeCategories?: boolean;
  fields?: (keyof T["values"] & string)[];
}

/**
 * Fetches specific content by ID, with support for various options and relations.
 *
 * @param idOrType The ID or type of the content to fetch.
 * @param core The XcoreCore instance.
 * @param options (Optional), options for fetching content.
 * @param options.locales (Optional), content locales,
 * if provided locales have fallback content locale in config it will be included in request too.
 * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
 * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
 * @param options.includeValues (Optional), include all content values in `values` field.
 * @param options.includeCategories (Optional), include categories in content.
 * @param options.fields (Optional), list of specific content fields to fetch.
 * `includeValues=true` param is automatically being included in request if `fields` param is specified.
 *
 * @returns An `BaseApiRequest` that resolves to a `ContentResponse<T>` or `null` if the content is not found.
 */
export const getContent = <T extends Content>(
  idOrType: number | T["type"],
  core: XcoreCore,
  { fields, locales, publishedOnly, ...options }: GetContentOptions<T> = {}
): BaseApiRequest<T | null> => {
  const hasFieldsSelect = Array.isArray(fields) && fields.length > 0;

  const queryParams = {
    ...options,
    ...(hasFieldsSelect && { fields, includeValues: true }),
    ...resolveLocaleParams(core, locales),
    ...resolveFetchPublishedOnly(publishedOnly, core.authToken),
  };

  return core
    .fetch(`/api/content/${idOrType}${parseQueryParamsObject(queryParams)}`)
    .then(async (res) => await res.json())
    .then(async (content: T) => {
      content = resolveContentSubCategories(content);

      if (
        !content ||
        !process.env.NEXT_PUBLIC_FETCH_UNFETCHED_FIELDS_VALUES ||
        process.env.NEXT_PUBLIC_FETCH_UNFETCHED_FIELDS_VALUES === "false" ||
        !content?.values
      ) {
        return content as T | null;
      }

      return await resolveUnfetchedValuesToContentValuesForContent<T>(content, core);
    })
    .catch((err) => {
      if (err instanceof NotFoundError) {
        return null;
      }

      throw err;
    });
};
